import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import biApi from "../../../../../api/biApi";
import type { WorkbookRun } from "../../../../../api/types/workbookRuns.types";

const deleteRuns = withErrorHandling(biApi.deleteWorkbookRuns);

interface Props {
  runs: WorkbookRun[];
  onClose: () => void;
  onDeleted: (runIds: string[]) => void;
}

const DeleteRunsDialog = ({ runs, onClose, onDeleted }: Props) => {
  const { sendNotificationError, sendNotification } = useNotificationContext();
  const [deleting, setDeleting] = useState(false);

  const singleRun = runs.length === 1 ? runs[0] : undefined;

  const handleDeleteRuns = async () => {
    setDeleting(true);
    const runIds = runs.map((run) => run.id);
    const [, error] = await deleteRuns({ runIds });
    setDeleting(false);
    if (error) {
      logError(error, "[DeleteRunsDialog] deleteRuns");
      sendNotificationError(error.message);
      return;
    }
    sendNotification(`${pluralize("Run", runs.length)} deleted successfully`);
    onDeleted(runIds);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{`Delete ${pluralize("Run", runs.length)}`}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center", minWidth: 0 }}>
            <Typography variant="h6" sx={{ textAlign: "center", wordBreak: "break-word" }}>
              {singleRun ? (
                <>
                  Delete <strong>{singleRun.name}</strong> run?
                </>
              ) : (
                `Delete ${runs.length} runs?`
              )}
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {singleRun
                ? "You're about to delete the run and its workbook. This action is permanent and can't be undone."
                : "You're about to delete the runs and their workbooks. This action is permanent and can't be undone."}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleDeleteRuns}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteRunsDialog;
